import { type StepperRef, useIntl, type UserBasicRequirement } from '@capsicum/common';
import { RadioButtonInput, Stepper, useInfoModal } from '@capsicum/common-web';
import { InvestorTypeOptions, useUkSelfCategorisation } from '@capsicum/features/regulations/shared';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { match } from 'ts-pattern';

import { HighNetWorthInvestorForm } from './forms/HighNetWorthInvestorForm';
import { RestrictedInvestorDescription } from './forms/RestrictedInvestorDescription';
import { RestrictedInvestorForm } from './forms/RestrictedInvestorForm';

type UkSelfCategorisationAssessmentProps = {
	isForcedRequirement?: boolean;
	isUniqueRequirement: boolean;
	onDismiss: () => void;
	requirement: UserBasicRequirement;
};

export const UkSelfCategorisationAssessment = ({
	isForcedRequirement,
	isUniqueRequirement,
	onDismiss,
	requirement
}: UkSelfCategorisationAssessmentProps) => {
	const { $t } = useIntl();
	const stepperRef = useRef<StepperRef>(null);
	const { showInfoModal } = useInfoModal();

	const {
		confirmationModalDescription,
		confirmButton,
		disabledFirstButton,
		handleFormValidChange,
		handleFormValuesChange,
		investorOptions,
		isSubmitLoading,
		onModalConfirmPress,
		onSubmitSteps,
		selectedInvestorType,
		setIndex,
		setSelectedInvestorType,
		showModal,
		title
	} = useUkSelfCategorisation({ isUniqueRequirement, requirement });

	const Step1 = () => (
		<S.ContentContainer>
			<S.Title>{$t('jurisdictionRequirements.ukSelfCategorisation.statementTitle')}</S.Title>
			<S.RadioButtonsContainer>
				{investorOptions.map(item => (
					<RadioButtonInput
						checked={item.id === selectedInvestorType}
						item={item}
						key={item.id}
						pressableLabels
						testID={item.id}
						onPress={() => setSelectedInvestorType(item.id)}
					/>
				))}
			</S.RadioButtonsContainer>
		</S.ContentContainer>
	);

	const Step2 = useCallback(() => {
		return match(selectedInvestorType)
			.with(InvestorTypeOptions.RESTRICTED, () => (
				<S.ContentContainer>
					<RestrictedInvestorDescription />
				</S.ContentContainer>
			))
			.otherwise(() => (
				<S.ContentContainer>
					<HighNetWorthInvestorForm
						requirement={requirement}
						onFormValidChange={handleFormValidChange}
						onFormValuesChange={handleFormValuesChange}
					/>
				</S.ContentContainer>
			));
	}, [requirement, selectedInvestorType]);

	const Step3 = useCallback(() => {
		return (
			<S.ContentContainer>
				<RestrictedInvestorForm
					requirement={requirement}
					onFormValidChange={handleFormValidChange}
					onFormValuesChange={handleFormValuesChange}
				/>
			</S.ContentContainer>
		);
	}, [requirement]);

	const showConfirmationModal = () => {
		showInfoModal({
			buttonOnHandlers: {
				onPrimaryButtonHandler: onSubmitSteps,
				primaryInvokeOnDismiss: false
			},
			buttonTexts: {
				primaryButtonText: $t('global.button.accept'),
				secondaryButtonText: $t('global.button.decline')
			},
			image: 'warning',
			onDismiss: () => {
				setSelectedInvestorType(null);
				stepperRef.current?.moveToStepIndex(0);
			},
			subtitle: confirmationModalDescription,
			testID: 'uk-self-categorisation-confirmation-modal',
			title: title,
			variant: 'confirm'
		});
	};

	useEffect(() => {
		if (!isUniqueRequirement) {
			showInfoModal({
				buttonOnHandlers: {
					onPrimaryButtonHandler: onModalConfirmPress,
					primaryInvokeOnDismiss: false
				},
				buttonTexts: {
					primaryButtonText: $t('global.button.completeItNow')
				},
				image: 'questionnaire',
				isClosable: !isForcedRequirement,
				onDismiss,
				subtitle: $t('jurisdictionRequirements.ukSelfCategorisation.modal.description'),
				testID: 'uk-self-categorisation-introduction-modal',
				title: $t('jurisdictionRequirements.ukSelfCategorisation.modal.title'),
				variant: isForcedRequirement ? 'alert' : 'confirm'
			});
		}
	}, []);

	const steps = useMemo(() => {
		switch (selectedInvestorType) {
			case InvestorTypeOptions.RESTRICTED:
				return [Step1, Step2, Step3];
			case InvestorTypeOptions.HIGH_NET_WORTH:
				return [Step1, Step2];
			default:
				return [Step1];
		}
	}, [selectedInvestorType]);

	if (showModal) return <S.EmptyContainer />;

	return (
		<S.Wrapper>
			<Stepper
				customBackNavigation={onDismiss}
				disabledFirstButton={disabledFirstButton}
				firstButtonText={confirmButton}
				isLoading={isSubmitLoading}
				onlyButtonsNavigation={isForcedRequirement}
				ref={stepperRef}
				steps={steps}
				testID="uk-self-categorisation-stepper"
				onStepIndexChange={setIndex}
				onSubmitSteps={() => onSubmitSteps(showConfirmationModal)}
			/>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		flex: 1;
		height: 100%;
		padding: ${({ theme }) => theme.spacings.medium} 0;
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => theme.spacings.medium};
		}
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
	`;
	export const Title = styled.span`
		${({ theme }) => theme.fonts.headings.title5};
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const ContentContainer = styled.div`
		gap: ${({ theme }) => theme.spacings.large};
	`;

	export const Paragraph = styled.p`
		${({ theme }) => theme.fonts.text.text3};
	`;

	export const EmptyContainer = styled.div`
		flex: 1;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
	`;

	export const RadioButtonsContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.large};
		margin: ${({ theme }) => `${theme.spacings.small} 0`};
	`;

	export const ButtonWrapper = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;
}
